import pRetry from 'p-retry';
import * as semver from 'semver';
import * as utils from '../../utils';
import { getLogger } from '../../commons/logger';
import { WindowUtils } from '../utils/windowUtils';
import { ScreenshotUtils } from '../utils/screenshotUtils';
import { ImageCaptureUtils } from '../utils/imageCaptureUtils';
import { getSessionPlayer } from '../../commons/getSessionPlayerRequire';
import type { WebDriver } from '../webdriver';
import type { Step } from 'clickim/src/common/models/step';
import type { SessionTab } from 'clickim/src/background/session/tabService';
import type { ParameterStep } from 'clickim/src/common/models/ParameterStep';
import type { UrlObject } from 'clickim/src/background/portMatch/urlPartsBreaker';
import type { ITabService, TabInfo, ITabUtils } from 'clickim/src/background/session/ITabService';

const logger = getLogger('tab-service');

interface AddTabOptions {
    takeScreenshots?: boolean;
    skipLoadInfo?: boolean;
    loadInfo?: boolean;
    forceSwitch?: boolean;
}

export class TabService implements ITabService {
    public sessionTabs: Record<string | number, SessionTab> = {};
    private _utils: Record<string, ITabUtils> = {};
    private pendingTabs: Record<string, string> = {};
    private addedTabs: Record<string, Set<any>> = {};
    private addFrameHandler?: (tabId: number, url?: string, options?: { emptyPage?: boolean }) => Promise<void> | undefined;

    constructor(private readonly driver: WebDriver) {
    }

    public on() { /* noop */ }

    public tabCount(sessionId: string | number) {
        if (this.sessionTabs[sessionId]) {
            return this.sessionTabs[sessionId].tabCount;
        }
        return undefined;
    }

    public getAllOpenTabIds(sessionId: string | number) {
        const allTabInfos = this.getAllTabInfos(sessionId);
        return Object.keys(allTabInfos)
            .filter(tabId => !allTabInfos[tabId].isClosed);
    }

    /**
     * TODO: seems unused with new Applitools integration
     * Get last tab info for pixel validation
     *
     * @returns last tab info
     */
    private getActiveTabInfo(sessionId: string | number) {
        return this.sessionTabs[sessionId].lastActiveTabInfo;
    }

    private getAllTabIds(sessionId: string | number) {
        return Object.keys(this.getAllTabInfos(sessionId)).map(id => id);
    }

    public isSessionTab(sessionId: string | number, tabId: string) {
        return this.getAllTabIds(sessionId).includes(tabId);
    }

    public createSesion(sessionId: number) {
        if (this.sessionTabs[sessionId]) { // joining an existing session
            return;
        }
        this.addedTabs[sessionId] = new Set();
        this.sessionTabs[sessionId] = {
            pendingInfos: {},
            tabCount: 0,
            tabInfos: {},
        };
    }

    public setAddFrameHandlerCallBack(addFrameHandlerCb: (tabId: number, url?: string, options?: { emptyPage?: boolean }) => Promise<void> | undefined) {
        this.addFrameHandler = addFrameHandlerCb;
    }

    private getAllTabInfoStrings(sessionId: number) { // unused?
        const allIds = this.getAllTabIds(sessionId);
        return allIds.map(tabId => {
            const tabInfo = this.getTabInfo(sessionId, tabId);
            // eslint-disable-next-line max-len
            return `tabId=${tabId}, url=${tabInfo.url}, order=${tabInfo.order}, isMain=${tabInfo.isMain}, openerStepId=${tabInfo.openerStepId}, isClosed=${tabInfo.isClosed}, currentUrl: ${tabInfo.currentUrl}, lastUpdatedUrl: ${tabInfo.lastUpdatedUrl}`;
        });
    }

    public getAllTabInfos(sessionId: string | number) {
        return this.sessionTabs[sessionId].tabInfos;
    }

    public async addNewTab(sessionId: number, tabId: string | number, openerStepId?: string, options?: AddTabOptions) {
        if (this.addedTabs[sessionId].has(tabId)) {
            return undefined;
        }
        this.addedTabs[sessionId].add(tabId);
        logger.info(`Adding a new tab sessionId: ${sessionId}, tabId: ${tabId}, openerId: ${openerStepId}`);
        return this.addTab(sessionId, tabId, this.sessionTabs[sessionId].tabCount++, openerStepId, options);
    }

    public addOpenerStepId(sessionId: number, tabId: number, openerStepId: string) {
        this.sessionTabs[sessionId].tabInfos[tabId].openerStepId = openerStepId;
    }
    public addOpenerStep(sessionId: number, tabId: number, openerStep: any) {
        this.sessionTabs[sessionId].tabInfos[tabId].openerStepId = openerStep.id;
        this.sessionTabs[sessionId].tabInfos[tabId].openerOriginalStepId = openerStep.originalStepId;
    }

    public fixMissingMainTab(sessionId: number) {
        const mainTab = this.getMainTabInfo(sessionId);
        if (mainTab) {
            return;
        }
        const allTabInfos = this.getAllTabInfos(sessionId);
        if (Object.keys(allTabInfos).length === 0) {
            // no tab infos 🤷
            return;
        }
        Object.values(this.getAllTabInfos(sessionId))[0].isMain = true;
    }

    async buildTabInfo(sessionId: number, tabId: string | number, order: number, openerStepId?: string, options: { checkForMainTab?: boolean; takeScreenshots?: boolean } = {}) {
        const tab = await this.getTabDetails(tabId, sessionId, options);
        const infoId = utils.guid();
        function isMainTab(tabService: TabService) {
            if (options.checkForMainTab) {
                return tab.isMainTab;
            }

            if (!tab.isMainTab || tab.isMainTab === 'unknown') {
                const missingMainTab = !tabService.getMainTabInfo(sessionId);
                return missingMainTab;
            }
            return tab.isMainTab;
        }
        this.sessionTabs[sessionId].tabInfos[tabId] = {
            infoId,
            url: tab.url,
            title: tab.title,
            favIconUrl: (tab as any).favIconUrl,
            order,
            from: this.getTabInfo(sessionId, (tab as any).openerTabId),
            isMain: isMainTab(this) as boolean,
            openerStepId,
        } as TabInfo;
        return infoId;
    }

    async addTab(sessionId: number, id: string | number, order: number, openerStepId?: string, options: { takeScreenshots?: boolean } = {}) {
        const infoId = await this.buildTabInfo(sessionId, id, order, openerStepId, options);
        const _windowUtils = new WindowUtils(id as number, this.driver);
        this._utils[infoId] = {
            attachDebugger: () => Promise.resolve(),
            detachDebugger: () => Promise.resolve(),
            onDebuggerDetached: () => { /** noop */ },
            tabId: id,
            domUtils: { getDOM: async () => undefined },
            windowUtils: _windowUtils,
            imageCaptureUtils: new ImageCaptureUtils(
                id,
                _windowUtils,
                new ScreenshotUtils(id as string, this.driver, { takeScreenshots: options.takeScreenshots }),
            ),
        };
    }

    public getTabUtilsByTabIdAndSessionId(sessionId: number, tabId: string | number) {
        const tabInfo = this.sessionTabs[sessionId].tabInfos[tabId];
        return this._utils[tabInfo.infoId];
    }

    public getTabUtilsByTabId(tabId: string | number) {
        const infoId = Object.keys(this._utils).find(uId => this._utils[uId].tabId === tabId)!;
        return this._utils[infoId];
    }

    public getTabInfo(sessionId: number, id: string | number) {
        return this.sessionTabs[sessionId].tabInfos[id];
    }

    public getTabUtils(sessionId: number, tabInfo?: TabInfo) {
        if (!tabInfo) {
            return this.getMainTabUtils(sessionId);
        }

        if (this._utils[tabInfo.infoId]) {
            return this._utils[tabInfo.infoId];
        }

        if (tabInfo.isMain) {
            return this.getMainTabUtils(sessionId);
        }

        const infos = this.getAllTabInfos(sessionId);
        const nonMainTabs = Object.keys(infos)
            .map(tabId => infos[tabId])
            .filter(info => !info.isMain);
        if (nonMainTabs.length === 1) {
            return this._utils[nonMainTabs[0].infoId];
        }

        const sameTabs = Object.keys(sessionId)
            // @ts-expect-error this is very weird, and seems off
            .map(key => sessionId[key])
            .filter(info => this.isSameTab(sessionId, tabInfo, info));
        if (sameTabs.length > 0) {
            return this._utils[sameTabs[0].infoId];
        }

        // if nothing else
        return this.getMainTabUtils(sessionId);
    }

    private exactUrlMatch(first: TabInfo, second: TabInfo, allUrls: string[]) {
        const exactUrlMatch = allUrls
            .filter(url => url === second.url);

        if ((first.url === second.url || first.currentUrl === second.url || (first.currentUrl && (first.currentUrl === second.currentUrl))) && (exactUrlMatch.length === 1)) {
            return true;
        }
        return false;
    }

    private singleExactMatchForParts(first: TabInfo, second: TabInfo, allUrls: string[], combinePartsFunction: (url: UrlObject) => string) {
        const { urlUtils } = getSessionPlayer();
        const firstUrlParts = urlUtils.urlBreaker(first.url || first.currentUrl);
        const secondUrlParts = urlUtils.urlBreaker(second.url || second.currentUrl);
        const firstPartsCombined = combinePartsFunction(firstUrlParts);
        const secondPartsCombined = combinePartsFunction(secondUrlParts);
        const allDomainAndPathMatches = allUrls.map(url => urlUtils.urlBreaker(url))
            .map(urlParts => combinePartsFunction(urlParts))
            .filter(combinedParts => combinedParts === firstPartsCombined);
        if ((firstPartsCombined === secondPartsCombined) && (allDomainAndPathMatches.length === 1)) {
            return true;
        }
        return false;
    }

    private isSameTab(sessionId: number, first: TabInfo, second: TabInfo) {
        const { tabMatcher } = getSessionPlayer();
        if (tabMatcher) {
            const infos = this.getAllTabInfos(sessionId);
            const allTabInfos = Object.keys(infos).map(tabId => infos[tabId]);
            return tabMatcher.isSameTab(allTabInfos, first, second);
        }
        // flow:
        // both main => exact match => exact match for domain and path => exact match for domain path and hash
        // => order (legacy data)
        if (first.isMain && second.isMain) {
            return true;
        }

        if (first.openerStepId && second.openerStepId && first.openerStepId === second.openerStepId) {
            return true;
        }

        const infos = this.getAllTabInfos(sessionId);
        const allTopFrameUrls = Object.keys(infos)
            .map(tabId => infos[tabId].url);

        if (this.exactUrlMatch(first, second, allTopFrameUrls)) {
            return true;
        }

        const combineDomainAndPath = (urlParts: UrlObject) => (`${urlParts.domain}/${urlParts.path.join('/')}`);
        if (this.singleExactMatchForParts(first, second, allTopFrameUrls, combineDomainAndPath)) {
            return true;
        }

        const combineDomainPathAndHash = (urlParts: UrlObject) => (`${urlParts.domain}/${urlParts.path.join('/')}#${urlParts.hash}`);
        if (this.singleExactMatchForParts(first, second, allTopFrameUrls, combineDomainPathAndHash)) {
            return true;
        }

        if (first.order === second.order) {
            return true;
        }

        return false;
    }

    public getMainTabInfo(sessionId: number) {
        const infos = this.getAllTabInfos(sessionId);
        return Object.keys(infos)
            .map(id => infos[id])
            .find(tabInfo => tabInfo.isMain);
    }

    public getMainTabUtils(sessionId: number): ITabUtils {
        const mainTabInfo = this.getMainTabInfo(sessionId);
        if (!mainTabInfo) {
            return {} as ITabUtils;
        }
        return this.getTabUtils(sessionId, mainTabInfo);
    }

    public removeTabInfo(sessionId: number, tabId: string | number) {
        const infos = this.getAllTabInfos(sessionId);
        const info = infos[tabId];
        delete this.sessionTabs[sessionId].tabInfos[tabId];
        delete this._utils[info.infoId];
        this.sessionTabs[sessionId].tabCount--;
    }

    public getMainTabId(sessionId: number) {
        const infos = this.getAllTabInfos(sessionId);
        return Object.keys(infos).find(id => infos[id].isMain) as any as number;
    }

    public async isMainTabExists(sessionId: number) {
        const mainTabId = this.getMainTabId(sessionId);
        return Boolean(mainTabId);
    }

    public clearAllTabs(sessionId: number) {
        const infos = this.getAllTabInfos(sessionId);

        this.sessionTabs[sessionId].tabCount = 0;
        Object.keys(infos)
            .forEach(tabId => this.removeTabInfo(sessionId, tabId));
    }

    public clearNonMainTabs(sessionId: number) { // unused?
        const infos = this.getAllTabInfos(sessionId);
        Object.keys(infos)
            .filter(tabId => !infos[tabId].isMain)
            .forEach(tabId => this.removeTabInfo(sessionId, tabId));

        this.sessionTabs[sessionId].tabCount = 1;
    }

    async switchTab(tabId: string | number, sessionId: number, { forceSwitch = false } = {}) {
        const tabCount = this.sessionTabs[sessionId] ? this.sessionTabs[sessionId].tabCount : 1;
        // looking at session tabs here and tabCount only works if we already "know" all the tabs, so we
        // opt out of it in hybrid steps and other cases.
        if ((typeof tabCount === 'number' && tabCount > 1) || forceSwitch) {
            return this.driver.switchTab(tabId as string);
        }
        return undefined;
    }

    async getTabDetails(tabId: string | number, sessionId: number, options: any = {}) {
        try {
            await this.switchTab(tabId, sessionId, options);
            if (options.skipLoadInfo) { // the tab title and url are not interesting yet
                return { title: '', url: '' };
            }
            let mainTabPromise: Promise<boolean | undefined | 'unknown'> = Promise.resolve('unknown');
            if (options.checkForMainTab) {
                mainTabPromise = this.driver.executeJS<() => boolean | undefined>('return window.__isMainTestimTab').then(r => r.value);
            }

            try {
                const [title, url, isMainTab] = await Promise.all([this.driver.getTitle(), this.driver.getUrl(), mainTabPromise]);
                return { title, url, isMainTab };
            } catch (err) {
                logger.error('failed to get url or title', { err });
            }
        } catch (err) {
            logger.error('failed to switch to tab', { tabId, err });
        }
        return { title: '', url: '' };
    }

    private async getUnregisteredTabId(sessionId: number) {
        const ids = await this.driver.getTabIds();
        return ids.find(tabId => !this.getAllTabIds(sessionId).includes(tabId));
    }

    private async waitForTabToOpen(sessionId: number): Promise<string> { //unused?
        const newId = await this.getUnregisteredTabId(sessionId);
        if (newId) {
            return newId;
        }
        await utils.delay(500);
        return await this.waitForTabToOpen(sessionId);
    }

    private async tryToAddTab(sessionId: number) {
        if (this.pendingTabs[sessionId]) {
            // don't mess with the main flow
            return;
        }
        const newId = await this.getUnregisteredTabId(sessionId);
        if (!newId) {
            return;
        }
        await this.addNewTab(sessionId, newId);
        await this.addFrameHandler!(newId as any as number);
        Object.assign(this.sessionTabs[sessionId], { currentTab: null });
    }

    public async addNewPopup(id: number, openerStepId: string) {
        const tabInfos = this.getAllTabInfos(id);
        const stepWasAdded = Object.keys(tabInfos).find(tabId => tabInfos[tabId].openerStepId === openerStepId);
        if (stepWasAdded) {
            return;
        }
        if (this.pendingTabs[id]) {
            logger.info(`overriding opener step id from ${this.pendingTabs[id]} to ${openerStepId}`);
            this.pendingTabs[id] = openerStepId;
            return;
        }
        this.pendingTabs[id] = openerStepId;
        const newTabId = await this.waitForTabToOpen(id);
        await this.addNewTab(id, newTabId, this.pendingTabs[id]);
        this.addFrameHandler!(newTabId as any as number);
        delete this.pendingTabs[id];
        Object.assign(this.sessionTabs[id], { currentTab: null });
    }

    private async waitToPendingTabs(id: number, openerStepId: string | null | undefined) {
        if (!openerStepId) {
            return undefined;
        }

        return pRetry(() => {
            if (this.pendingTabs[id] !== openerStepId) {
                return;
            }
            throw new Error('awaiting pending tabs');
        }, { retries: 5, factor: 1, minTimeout: 500, maxTimeout: 500 }).catch(() => undefined);
    }

    public async isMainTabIncognito() {
        return false;
    }

    private isInvalidStepVersion(step: Step) {
        const isOldVersion = semver.lt(step._version || step.version, '1.2.0');
        const hasParams = !!(step as ParameterStep).parameterValues;
        const hasBadLocateParams = hasParams &&
            (step as ParameterStep).parameterValues
                .some(param => param.type === 'locate' && !param.frameLocators);

        return isOldVersion && (!hasParams || hasBadLocateParams);
    }

    public async getTabIdByTabInfo(sessionId: number, step: Step): Promise<number> {
        const { tabMatcher, commonConstants } = getSessionPlayer();
        if (this.isInvalidStepVersion(step)) {
            throw Object.assign(new Error(''), {
                success: false,
                shouldRetry: false,
                errorType: commonConstants.stepResult.INVALID_TEST_VERSION,
            });
        }
        if (step.useCurrentTab?.()) {
            const tabId = await this.driver.getCurrentTabId();
            if (tabId) return tabId as any as number;
            const mainTabId = this.getMainTabId(sessionId);
            if (mainTabId) return mainTabId;
            throw new Error('Current tab not found');
        }
        const openerStepId = step.tabInfo?.openerStepId;
        await this.waitToPendingTabs(sessionId, openerStepId);
        await this.tryToAddTab(sessionId);
        const allTabInfos = this.getAllTabIds(sessionId)
            .map((_tabId): TabInfo => ({ ...this.getTabInfo(sessionId, _tabId), tabId: _tabId as any }))
            .filter((tabInfo) => !tabInfo.isClosed);
        const tabId = tabMatcher.matchTabs(step, allTabInfos);

        if (!tabId) {
            throw new Error('No tab ID found');
        }
        if ((this.sessionTabs[sessionId] as any).currentTab === tabId) {
            return tabId;
        }
        try {
            await this.switchTab(tabId, sessionId);
            Object.assign(this.sessionTabs[sessionId], { currentTab: tabId });
            return tabId;
        } catch (err) {
            const windowClosedErrors = ['no such window', 'no window found', 'the window could not be found'];
            if (err.message && windowClosedErrors.some(errorString => err.message.toLowerCase().includes(errorString))) {
                this.sessionTabs[sessionId].tabCount--;
                this.sessionTabs[sessionId].tabInfos[tabId].isClosed = true;
                return this.getTabIdByTabInfo(sessionId, step);
            }
            throw err;
        }
    }
}
